// home 页的「平台」实现：工程列表/新建/打开全部走 IndexedDB + OPFS（无后端）。
// 接口对齐 electron-rendering 的 app/home/platform.js 与 web/platform/home.js。
import * as db from './db.js';
import * as opfs from './opfs.js';
import { sitePath } from '../../web/platform/site_base.js';

export function randomUUID() {
    return crypto.randomUUID();
}

export async function listProjects() {
    const all = await db.getAll('projects');
    return all.sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
}

export async function createProject({ name, protocol }) {
    const id = protocol?.id || randomUUID();
    const project = { id, name, protocol, updatedAt: new Date().toISOString() };
    await db.put('projects', project);
    // 协议也落一份到 OPFS（文件系统存文件，数据库存索引），供渲染按路径读取。
    await opfs.putFile(`/projects/${id}/protocol.json`, JSON.stringify(protocol, null, 2));
    return project;
}

export async function deleteProject(id) {
    await db.del('projects', id);
    await opfs.remove(`/projects/${id}`);
}

export async function renameProject(id, name) {
    const project = await db.get('projects', id);
    if (!project) throw new Error('工程不存在');
    const updated = { ...project, name, updatedAt: new Date().toISOString() };
    await db.put('projects', updated);
    return updated;
}

/**
 * 新建或复用指定 ID 的工程。
 * aiMuse 剧集入口用「项目 ID + 章节 ID」生成稳定工程 ID，
 * 同一浏览器再次进入同一集时回到上次的时间轴，不会重复建工程。
 */
export async function ensureProject({ id, name, width, height }) {
    const safeWidth = Number.isFinite(width) && width > 0 ? Math.round(width) : 1080;
    const safeHeight = Number.isFinite(height) && height > 0 ? Math.round(height) : 1920;
    const existing = await db.get('projects', id);
    if (existing) {
        // 复用时只同步剧集名称，不刷新 updatedAt：
        // 本地最后编辑时间要保留，入口页才能和服务端草稿的 updatedAt 正确比较。
        const nextName = name || existing.name || '未命名剧集';
        if (nextName !== existing.name) {
            const updated = { ...existing, name: nextName };
            await db.put('projects', updated);
            return updated;
        }
        return existing;
    }
    const protocol = {
        id, duration: 5_000_000, fps: 30,
        canvas_config: { width: safeWidth, height: safeHeight, ratio: `${safeWidth}:${safeHeight}` },
        // 默认跟随时间轴视频自动适应画布，用户可在播放器控制栏手动固定比例。
        canvas_fit: 'auto',
        tracks: [], materials: {},
    };
    const project = { id, name: name || '未命名剧集', protocol, updatedAt: new Date().toISOString() };
    await db.put('projects', project);
    await opfs.putFile(`/projects/${id}/protocol.json`, JSON.stringify(protocol, null, 2));
    return project;
}

/** 读取本地工程记录。 */
export async function getProject(id) {
    return db.get('projects', id);
}

function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 把旧「每章一个工程」协议里的素材路径改写到项目级工程。
 * 旧路径形如 /projects/muse-<项目ID>-<章节ID>/uploads/...，共享工程统一为 /projects/muse-<项目ID>/...
 */
export function rewriteLegacyProjectPaths(value, projectId, targetProjectId) {
    const pattern = new RegExp(`/projects/${escapeRegExp(`muse-${projectId}`)}-[^/]+/`, 'g');
    const replacement = `/projects/${targetProjectId}/`;
    const walk = (input) => {
        if (typeof input === 'string') return input.replace(pattern, replacement);
        if (Array.isArray(input)) return input.map(walk);
        if (input && typeof input === 'object') {
            const result = {};
            for (const [key, item] of Object.entries(input)) result[key] = walk(item);
            return result;
        }
        return input;
    };
    const protocol = walk(value);
    if (protocol && typeof protocol === 'object') {
        protocol.id = targetProjectId;
    }
    return protocol;
}

/** 递归复制 OPFS 工程目录；跳过可再生的缓存和会自动重建的 sidecar。 */
async function copyProjectFiles(fromId, toId) {
    const sidecars = new Set(['protocol.json', 'draft_meta_info.json', 'draft_virtual_store.json']);
    const copyDirectory = async (fromPath, toPath) => {
        const entries = await opfs.list(fromPath);
        for (const entry of entries) {
            if (entry.name === '.cache') continue;
            if (entry.kind === 'directory') {
                await copyDirectory(`${fromPath}/${entry.name}`, `${toPath}/${entry.name}`);
                continue;
            }
            if (sidecars.has(entry.name)) continue;
            const file = await opfs.getFile(`${fromPath}/${entry.name}`);
            if (file) await opfs.putFile(`${toPath}/${entry.name}`, file);
        }
    };
    await copyDirectory(`/projects/${fromId}`, `/projects/${toId}`);
}

/** 判断旧工程协议里是否已有片段或素材，避免把空白章节工程当成迁移源。 */
function protocolHasContent(protocol) {
    const protocols = [protocol, ...(Array.isArray(protocol?.muse_timelines)
        ? protocol.muse_timelines.map(item => item?.protocol)
        : [])];
    return protocols.some(item => (
        (Array.isArray(item?.tracks) && item.tracks.some(track => (track?.segments || []).length > 0))
        || (item?.materials && typeof item.materials === 'object'
            && Object.values(item.materials).some(list => Array.isArray(list) && list.length > 0))
    ));
}

/**
 * 首次进入项目级共享工程时，把该项目下最近编辑的章节工程迁移过来。
 * 优先选「最近编辑且已有内容」的旧工程，避免用空白工程覆盖用户之前的剪辑。
 * 旧工程与旧文件保留不删除，便于回滚；迁移后素材路径统一改写为项目级目录。
 */
export async function migrateLegacyChapterProject({ projectId, targetId, name, status }) {
    const existingTarget = await db.get('projects', targetId);
    if (existingTarget) return null;
    const prefix = `muse-${projectId}-`;
    const legacyProjects = (await listProjects())
        .filter(project => String(project.id || '').startsWith(prefix))
        .sort((left, right) => String(right.updatedAt || '').localeCompare(String(left.updatedAt || '')));
    const source = legacyProjects.find(project => protocolHasContent(project.protocol))
        || legacyProjects[0];
    if (!source) return null;
    // 没有协议快照时交给 ensureProject 创建空工程，避免迁移出无法加载的半成品。
    if (!source.protocol || typeof source.protocol !== 'object') return null;

    status?.(`正在迁移「${source.name || source.id}」到共享时间线…`);
    const protocol = rewriteLegacyProjectPaths(source.protocol, projectId, targetId);
    await copyProjectFiles(source.id, targetId);
    await opfs.putFile(`/projects/${targetId}/protocol.json`, JSON.stringify(protocol, null, 2));
    const project = {
        id: targetId,
        name: name || source.name || '未命名项目',
        protocol,
        updatedAt: new Date().toISOString(),
    };
    await db.put('projects', project);
    return project;
}

/**
 * 用服务端剪辑草稿覆盖本地工程协议。
 * 入口页在「服务端草稿更新」或「本地首次进入、还没有素材」时调用。
 */
export async function applyRemoteDraft(id, protocol, updatedAt) {
    const existing = await db.get('projects', id);
    if (!existing) {
        throw new Error('工程不存在');
    }
    const nextProtocol = { ...(protocol || {}), id };
    const nextUpdatedAt = updatedAt ? new Date(updatedAt).toISOString() : new Date().toISOString();
    const updated = { ...existing, protocol: nextProtocol, updatedAt: nextUpdatedAt };
    await db.put('projects', updated);
    await opfs.putFile(`/projects/${id}/protocol.json`, JSON.stringify(nextProtocol, null, 2));
    return updated;
}

/** 打开工程；options.embed 表示当前处于 aiMuse 页面的内嵌模式。 */
export async function openProject(project, options = {}) {
    const id = project.id || project.uuid;
    const params = new URLSearchParams({ project: id });
    if (options.embed) params.set('embed', '1');
    location.assign(`${sitePath('/shell.html')}?${params.toString()}`);
}

export async function openProjectFromFile() {
    // OPFS 无法跨页复用文件选择器；暂不支持从本地文件打开工程。
    return null;
}

export function openResourceProject() {
    // 资源创作聊天室（后续接入 deepagents 资源会话）。
    throw new Error('资源创作后续接入');
}

export function onRefresh(callback) {
    window.addEventListener('pageshow', callback);
}

export default {
    randomUUID,
    listProjects,
    createProject,
    ensureProject,
    getProject,
    migrateLegacyChapterProject,
    rewriteLegacyProjectPaths,
    applyRemoteDraft,
    openProject,
    openProjectFromFile,
    openResourceProject,
    onRefresh,
};
